import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import axios from "axios";
import {Status} from "./status";
import {Story} from "./story";
import {headers} from "./headers";
import {Tag} from "./fields";
import {host} from "./api";

export interface StoryState {
    stories: Array<Story>,
    status: Status,
    story: Story | undefined,
    storyFull: Story | undefined,
    error: string | null | undefined,
    needRefresh: boolean
}

const initialState: StoryState = {
    stories: [],
    status: Status.Idle,
    story: undefined,
    storyFull: undefined,
    error: null,
    needRefresh: true
}

export const fetchStories = createAsyncThunk('stories', async (projectId: number | undefined) => {
    const response = await axios.get(`${host}/projects/${projectId}/stories`, {
        headers: headers(projectId?.toString())
    })
    return response.data
})

export const fetchStory = createAsyncThunk('fetchStory', async (params: { projectId: number, storyId: number }) => {
    const response = await axios.get(`${host}/stories/${params.storyId}`, {
        headers: headers(params.projectId?.toString())
    })
    return response.data
})

export const fetchFullStory = createAsyncThunk('fetchFullStory', async (params: {
    projectId: number,
    storyId: number
}) => {
    const response = await axios.get(`${host}/stories/${params.storyId}/full`, {
        headers: headers(params.projectId?.toString())
    })
    return response.data
})

export const exportStory = createAsyncThunk('exportStory', async (params: { projectId: number, storyId: number }) => {
    const response = await axios.get(`${host}/stories/${params.storyId}/export`, {
        headers: headers(params.projectId?.toString()),
        responseType: 'blob'
    })
    return {data: response.data, storyId: params.storyId}
})

export const saveStory = createAsyncThunk('saveStory', async (params: { projectId: number, story: Story }) => {
    const response = await axios.post(`${host}/stories`,
        params.story,
        {
            headers: headers(params.projectId?.toString())
        })
    return response.data
})

export const deleteStory = createAsyncThunk('deleteStory', async (params: { projectId: number, storyId: number }) => {
    const response = await axios.delete(`${host}/stories/${params.storyId}`,
        {
            headers: headers(params.projectId?.toString())
        })
    return response.data
})

export const removeStoryTag = createAsyncThunk('removeStoryTag', async (params: {
    projectId: number,
    storyId: number,
    tagId: number
}) => {
    const response = await axios.delete(`${host}/stories/${params.storyId}/tags/${params.tagId}`,
        {
            headers: headers(params.projectId.toString())
        })
    return response.data
})

export const addStoryTag = createAsyncThunk('addStoryTag', async (params: {
    projectId: number,
    storyId: number,
    tag: Tag
}) => {
    const response = await axios.post(`${host}/stories/${params.storyId}/tags`,
        params.tag,
        {
            headers: headers(params.projectId.toString())
        })
    return response.data
})

export const storiesSlice = createSlice({
    name: 'stories',
    initialState,
    reducers: {
        resetStatus: (state) => {
            state.status = Status.Idle
        }
    },
    extraReducers(builder) {
        builder
            .addCase(fetchStories.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(fetchStories.fulfilled, (state, action) => {
                state.status = Status.Idle
                state.stories = action.payload
                state.needRefresh = false
            })
            .addCase(fetchStories.rejected, (state, action) => {
                state.status = Status.Failed
                state.error = action.error.message
                state.stories = []
            })
            .addCase(removeStoryTag.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(removeStoryTag.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(addStoryTag.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(addStoryTag.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveStory.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveStory.fulfilled, (state, action) => {
                state.status = Status.Idle
                state.story = action.payload
                state.needRefresh = true
            })
            .addCase(deleteStory.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteStory.fulfilled, (state, action) => {
                state.story = undefined
                state.status = Status.Idle
                state.needRefresh = true
            })
            .addCase(fetchStory.pending, (state, action) => {
                state.status = Status.Loading
                state.story = undefined
            })
            .addCase(fetchStory.fulfilled, (state, action) => {
                state.story = action.payload
                state.status = Status.Idle
            })
            .addCase(fetchFullStory.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(fetchFullStory.fulfilled, (state, action) => {
                state.status = Status.Idle
                state.storyFull = action.payload
            })
            .addCase(exportStory.pending, (state, action) => {
                state.status = Status.Exporting
            })
            .addCase(exportStory.fulfilled, (state, action) => {
                state.status = Status.Idle
                const link = document.createElement('a'); // Create a temporary <a> element
                link.href = window.URL.createObjectURL(action.payload.data);
                link.download = "novel_" + action.payload.storyId + `.zip`; // Set the download attribute
                document.body.appendChild(link);
                link.click(); // Programmatically click the link
                link.remove(); // Clean up after download
            })
    }
})

async function blob2string(blob: any) {
    return new TextDecoder().decode(await blob.arrayBuffer());
}

export const {resetStatus} = storiesSlice.actions
export default storiesSlice.reducer;